import React, { useEffect, useMemo, useState } from 'react';

import ExpLabel from '@exp/components/localization/ExpLabel';
import classes from '@exp/utilities/buildClassName';

import Modes, { ModeConfig, ModeContext } from '../constants/modes';
import { useAppSelector } from '../redux/hooks';
import { selectConfig, selectContext, selectPageOverviews } from '../redux/context';
import { useSendEvent } from '../utilities/useAnalytics';
import { noop } from '../utilities/utilities';
import { TemplateProps } from './types';
import Backdrop from './Backdrop';

import './AccordionLayout.less';

const AnalyticEvents = {
  ONBOARDING_EDIT_SETUP: 'ONBOARDING_EDIT_SETUP',
  ONBOARDING_QUALIFY_CLICK: 'ONBOARDING_QUALIFY_CLICK',
  ONBOARDING_QUALIFY_FORCED: 'ONBOARDING_QUALIFY_FORCED',
};

type PageOverview = {
  name: string;
  completionContextProperty: string;
  header: {
    defaultMessage: string;
    messageKey: string;
  };
  headerCompleted?: {
    defaultMessage: string;
    messageKey: string;
  };
  editable?: boolean;
  suppressEditButton?: boolean;
};

type AccordionHeaderProps = {
  currentIndex: number;
  goTo: (index: number) => void;
  index: number;
  overview: PageOverview;
};

const AccordionHeader = ({ currentIndex, goTo, index, overview }: AccordionHeaderProps) => {
  const context: ModeContext = useAppSelector(selectContext);
  const sendEvent = useSendEvent({ mode: context.mode });

  return (
    <div
      className={classes('accordion-item-header', index === currentIndex && 'active')}
      onClick={!context[overview.completionContextProperty] || overview.editable ? () => goTo(index) : undefined}
    >
      <div className={classes('index-marker', 'check', context[overview.completionContextProperty] && 'complete')}>
        <i className="exp-ux-check exp-ux-large" />
      </div>
      <div className="index-marker">
        <span>{index + 1}</span>
      </div>
      {!context[overview.completionContextProperty] ? (
        <ExpLabel
          className="accordion-header"
          defaultMessage={overview.header.defaultMessage}
          messageKey={overview.header.messageKey}
        />
      ) : (
        <ExpLabel
          className="accordion-header"
          defaultMessage={overview.headerCompleted?.defaultMessage}
          messageKey={overview.headerCompleted?.messageKey}
        />
      )}
      {overview.editable && !overview.suppressEditButton && [overview.completionContextProperty] && (
        <button
          className="link-primary edit-button"
          onClick={() => {
            sendEvent(AnalyticEvents.ONBOARDING_EDIT_SETUP, { page: overview.name });
          }}
          type="button"
        >
          <ExpLabel defaultMessage="Edit" messageKey="onb.accordion.edit" />
        </button>
      )}
    </div>
  );
};

AccordionHeader.displayName = 'AccordionHeader';

const AccordionLayout = React.forwardRef<HTMLDivElement, TemplateProps>(
  ({ children = null, currentPageName = null, goTo = noop }, ref) => {
    const overviews: PageOverview[] = useAppSelector(selectPageOverviews);
    const context: ModeContext = useAppSelector(selectContext);
    const { editSetupMode, qualifyDestination }: ModeConfig = useAppSelector(selectConfig);
    const sendEvent = useSendEvent({ mode: context.mode });
    const [loading, setLoading] = useState(true);

    const currentIndex = overviews?.findIndex((p) => p.name === currentPageName) || 0;

    useEffect(() => {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 50);
    }, [currentPageName]);

    const allComplete = useMemo(
      () => overviews?.length > 0 && overviews.map((o) => context[o.completionContextProperty]).every((c) => c),
      [overviews, context],
    );

    useEffect(() => {
      if (allComplete && !editSetupMode) {
        // redirect to qualify
        sendEvent(AnalyticEvents.ONBOARDING_QUALIFY_FORCED);
        window.location.href = qualifyDestination;
      }
    }, [allComplete, editSetupMode, qualifyDestination, sendEvent]);

    return (
      <div className="exp-onb-accordion" ref={ref}>
        <Backdrop
          stepCount={overviews?.length || 0}
          completedCount={overviews?.filter((o) => context[o.completionContextProperty]).length || 0}
        />
        <div className="exp-onb-accordion-container">
          <div className="accordion-context-header-target" />
          {overviews?.map(
            (overview, i) =>
              overview.name !== currentPageName && (
                <div
                  className={classes('accordion-item', context[overview.completionContextProperty] && 'complete')}
                  key={overview.name}
                  style={{ order: i }}
                >
                  <AccordionHeader currentIndex={currentIndex} goTo={goTo} index={i} overview={overview} />
                </div>
              ),
          )}
          <div className="accordion-item" style={{ order: currentIndex }}>
            {overviews?.[currentIndex] && (
              <AccordionHeader
                currentIndex={currentIndex}
                goTo={goTo}
                index={currentIndex}
                overview={overviews[currentIndex]}
              />
            )}
            <div
              className={classes(
                'accordion-item-page',
                'accordion-item-content',
                context[overviews?.[currentIndex]?.completionContextProperty] && 'complete',
                loading && 'loading',
              )}
            >
              {children}
            </div>
          </div>
          {((allComplete && context.mode === Modes.setup.name) || editSetupMode) && (
            <div className="flow-actions">
              <button
                className="btn-outline qualify"
                disabled={overviews?.length > 0 && !allComplete}
                onClick={() => {
                  sendEvent(AnalyticEvents.ONBOARDING_QUALIFY_CLICK);
                  window.location.href = qualifyDestination;
                }}
                type="button"
              >
                <ExpLabel defaultMessage="Qualify" messageKey="onb.accordion.qualify" />
                <i className="exp-ux-arrow exp-ux-small" />
              </button>
            </div>
          )}
        </div>
      </div>
    );
  },
);

AccordionLayout.displayName = 'AccordionLayout';

export default AccordionLayout;
